import * as React from "react";
import tw, { styled } from "twin.macro";
import * as Sentry from "@sentry/react";
import { BUSHA_URL } from "app/constants/variables";

import { ReactComponent as BushaLogo } from "app/assets/logo/busha-logo.svg";
import { ReactComponent as CloseIcon } from "app/assets/icons/close.svg";
import CancelModal from "./CancelModal";
import ErrorWidget from "./ErrorWidget";
import Text from "app/styles/Text";
import { useAppState } from "app/providers/AppStateProvider";
import BackButton from "./BackButton";

interface Props {
  title?: React.ReactNode;
  pageOnly?: boolean;
  className?: string;
  footer?: React.ReactNode;
  onBack?: () => void;
  noPadding?: boolean;
  loading?: boolean;
}
export default function Page(props: React.PropsWithChildren<Props>) {
  const {
    children,
    title,
    footer,
    noPadding,
    loading,
    onBack,
    ...rest
  } = props;

  const { displayMode } = useAppState();

  const [showCancelModal, setShowCancelModal] = React.useState(false);
  const onCloseCancelModal = () => {
    setShowCancelModal(false);
  };

  const isInline = displayMode === "INLINE";

  return (
    <>
      {!showCancelModal && (
        <Container $isInline={isInline} {...rest}>
          {displayMode === "INLINE" && (
            <button
              aria-label="Cancel payment"
              type="button"
              tw="bg-containment-tertiary rounded-full h-[32px] w-[32px] absolute right-[20px] top-[20px] flex items-center justify-center"
            >
              <CloseIcon
                tw="w-[16px] h-[16px]"
                onClick={() => setShowCancelModal(true)}
              />
            </button>
          )}


          <Frame noPadding={noPadding}>
            {(onBack || title) && (
              <div className="header">
                {onBack && (
                  <BackButton onClick={onBack} />
                )}
                {typeof title === "string" ? (
                  <>
                    {/* {canGoBack && (
                    <Button type="button">
                      <BackIcon
                        className="back"
                        color="#0D0D0D"
                        width={20}
                        height={20}
                        onClick={() => navigate(-1)}
                      />
                    </Button>
                  )} */}

                    {title && (
                      <h2 className="text-[24px] text-text-high">{title}</h2>
                    )}
                  </>
                ) : (
                  title
                )}
              </div>
            )}

            <div className="body">
              <Sentry.ErrorBoundary
                fallback={ErrorWidget}
                beforeCapture={(scope) => {
                  scope.setTag("location", "page");
                }}
              >
                {children}
              </Sentry.ErrorBoundary>
            </div>

            {footer && <div className="footer">{footer}</div>}
          </Frame>

          <Footer>
            <div tw="flex items-center gap-[8px]">
              <Text subdued>Secured by</Text>
              <a href={BUSHA_URL} target="_blank" rel="noopener noreferrer">
                <BushaLogo />
              </a>
            </div>
          </Footer>
        </Container>
      )}

      {showCancelModal && <CancelModal onClose={onCloseCancelModal} />}
    </>
  );
}

const Container = styled.section<{ $isInline?: boolean; $contentHeight?: number | null }>`
  ${tw`max-w-[480px] w-full justify-center pt-[48px] overflow-y-auto pb-[20px] h-full flex flex-col transition-[height] duration-300 ease-out`};
  ${({ $isInline }) =>
    $isInline &&
  tw`bg-containment-primary px-[16px] py-[20px] max-h-[90vh] rounded-[20px] max-w-[480px] animate-[pageScaleIn_0.3s_ease-in]`};
`;

const Frame = styled.div<{ noPadding?: boolean }>`
  ${tw`w-full flex flex-col grow overflow-y-auto`};

  > .header {
    ${tw`pb-[16px] flex items-center mobile:px-[16px] gap-[24px]`};

    > h2 {
      ${tw`text-[24px] font-semibold text-[#001003]`};
    }
  }

  > .body {
    ${tw`flex-grow max-h-full overflow-hidden`};
    ${(props) => props.noPadding && tw`px-0 mobile:p-0`}
  }

  > .footer {
    ${tw`px-[30px] py-[13.5px]`};
  }
`;

const Footer = styled.div`
  ${tw`flex gap-[16px] mt-[24px] items-center justify-center text-[1.4rem]`};

  img {
    ${tw` h-[16px]`};
  }
`;
